import { useState } from 'react';
import { Link } from 'react-router-dom';
import { submitMuqriRequest, useMuqriRequestStatus } from '@core/firebase/muqriRequest';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Check, Clock, Hourglass, ShieldCheck } from 'lucide-react';
import {
  EmptyState, IconTile, Notice, PageHeader, SoftCard, field, pill,
} from '@/components/PageKit';
import { cn } from '@/lib/cn';

/**
 * Applying to teach.
 *
 * The status is read live, not assumed: a returning applicant used to see a
 * blank form, resubmit, and be told "the request could not be sent" — false,
 * it had been sent. Only a REJECTED applicant sees the form again, which is
 * the one transition the rules allow back to pending.
 */
export default function RequestMuqriPage() {
  const { identity } = useAuth();
  const { t } = useLanguage();
  const { status, loading } = useMuqriRequestStatus(identity?.userId ?? null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const send = async () => {
    if (busy || !identity) return;
    setBusy(true);
    setFailed(false);
    try {
      await submitMuqriRequest(identity.userId, identity.displayName, note);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    // A form that flashes for a tick invites a duplicate application.
    return (
      <div data-testid="request-loading" className="mx-auto w-full max-w-4xl">
        <div className="card-soft h-40 animate-pulse" />
      </div>
    );
  }

  const canApply = status === null || status === 'rejected';

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6">
      <PageHeader title={t('muqriRequest.title')} />

      {/* Board t2: once sent, the state REPLACES the form. */}
      {status === 'pending' && (
        <SoftCard as="div">
          <EmptyState
            testId="request-pending"
            icon={<Hourglass />}
            title={t('muqriRequest.pending')}
            body={t('muqriRequest.pendingBody')}
          />
        </SoftCard>
      )}

      {status === 'approved' && (
        <SoftCard as="div">
          <EmptyState
            testId="request-approved"
            icon={<Check />}
            title={t('muqriRequest.approved')}
            body={t('muqriRequest.approvedBody')}
            action={(
              <Link to="/session/today" className={cn(pill.base, pill.md, pill.primary)}>
                {t('muqri.session')}
              </Link>
            )}
          />
        </SoftCard>
      )}

      {status === 'rejected' && (
        <Notice tone="danger" testId="request-rejected">{t('muqriRequest.rejected')}</Notice>
      )}

      {canApply && (
        <div className="grid items-start gap-6 md:grid-cols-5">
          <SoftCard className="space-y-5 md:col-span-3">
            <label className="block space-y-2">
              <span className="text-sm font-medium text-ink">{t('muqriRequest.noteLabel')}</span>
              <textarea
                data-testid="note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('muqriRequest.notePlaceholder')}
                rows={6}
                className={cn(field, 'resize-y')}
              />
            </label>

            {failed && <Notice tone="danger" testId="request-error">{t('muqriRequest.failed')}</Notice>}

            <button
              type="button"
              data-testid="submit-request"
              onClick={send}
              disabled={busy}
              aria-busy={busy}
              className={cn(pill.base, pill.lg, pill.primary, 'w-full')}
            >
              {t('muqriRequest.submit')}
            </button>
          </SoftCard>

          {/* What happens next, said before anything is sent. */}
          <aside className="card-soft space-y-4 p-5 md:col-span-2">
            <div className="flex items-start gap-3">
              <IconTile><Clock /></IconTile>
              <p className="text-sm text-ink">{t('muqriRequest.intro')}</p>
            </div>
            <div className="flex items-start gap-3">
              <IconTile><ShieldCheck /></IconTile>
              <p className="text-sm text-ink">{t('muqriRequest.verifyNote')}</p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
